import { useEffect } from "react";
import { useThree } from "@react-three/fiber";
import { DEG2RAD } from "three/src/math/MathUtils";
import { OrbitControls } from "@react-three/drei";
import { DeviceOrientationControlsCustom } from "./DeviceOrientationControlsCustom";
import { Locations } from "./Location";
import { lerp } from "../Util";

export function CustomControls({
	location,
	gyroSettings, lookSpeed = 0.125,
	needsReset, setNeedsReset,
	camHeightOffset = 0, camPosition = [0, 0, 0],
	inOverview, overviewCamTarget = [0, 0, 0]
}) {
	const camera = useThree(state => state.camera);

	const maxPolarAngle = (90.0 - 1.0) * DEG2RAD; // angle goes in the vertical axis, from looking top-down to bottom-up
	const overviewCamDistance = 28.0;
	const overviewAzimuthAngle = Locations[location].overviewAzimuthAngle * DEG2RAD;
	const overviewPolarAngle = Locations[location].overviewPolarAngle * DEG2RAD;

	useEffect(() => {
		if (inOverview) {
			camera.position.setFromSphericalCoords(overviewCamDistance, overviewPolarAngle, overviewAzimuthAngle);
		} else {
			camera.position.set(camPosition[0], camPosition[1] + camHeightOffset, camPosition[2]);
		}
	}, [location, inOverview, camHeightOffset, camPosition, camera]);

	if (inOverview) {
		return <OrbitControls
			makeDefault
			camera={camera} target={overviewCamTarget}
			maxPolarAngle={maxPolarAngle} enablePan={false}
			minDistance={overviewCamDistance} maxDistance={overviewCamDistance}
		/>;
	} else {
		if (gyroSettings.useGyro) {
            // Mobile controls: Device orientation
			return <DeviceOrientationControlsCustom
				camera={camera} location={location}
				needsReset={needsReset} setNeedsReset={setNeedsReset}
			/>;
		} else {
            // Browser controls: Street View style (Horizontal drag-to-pan)
            const targetX = camPosition[0];
            const targetY = camPosition[1] + camHeightOffset;
            const targetZ = camPosition[2] - 0.1; // Offset slightly to create a pivot point

			return <OrbitControls
				makeDefault
				camera={camera}
                target={[targetX, targetY, targetZ]}
                enableZoom={false}
                enablePan={false}
                enableDamping={true}
                dampingFactor={0.05}
                minDistance={0.1}
                maxDistance={0.1}
                // Lock the vertical axis to strictly horizontal span
                minPolarAngle={Math.PI / 2} 
                maxPolarAngle={Math.PI / 2}
                // Use a negative rotateSpeed if you want it to feel like you are "grabbing" the world
                rotateSpeed={-0.3} 
			/>;
		}
	}
}